/**
 * `prefers-reduced-motion` as React state (SPEC_CAPTURE_UX_R2 §4: a device/user that asked to avoid motion gets
 * the three static frames, never a loop). Live: a customer who flips the OS setting mid-visit is honoured on the
 * next render. A browser with no `matchMedia` reads as "motion is fine" — the CSS reduced-motion block still
 * applies to everything animated with keyframes.
 */

import { useSyncExternalStore } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

type Legacy = MediaQueryList & {
  addListener?: (l: (e: MediaQueryListEvent) => void) => void;
  removeListener?: (l: (e: MediaQueryListEvent) => void) => void;
};

function query(): Legacy | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  try {
    return window.matchMedia(QUERY) as Legacy;
  } catch {
    return null;
  }
}

function snapshot(): boolean {
  return query()?.matches === true;
}

function subscribe(l: () => void): () => void {
  const mq = query();
  if (!mq) return () => undefined;
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', l);
    return () => mq.removeEventListener('change', l);
  }
  mq.addListener?.(l);
  return () => mq.removeListener?.(l);
}

export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}
